import React from 'react';
import { Button, Table, TableBody } from '@insurancemenu/imenu-components-lib';
import { useRootStore } from 'store/rootStore';
import { useHistory, useParams } from 'react-router-dom';
import { runInAction } from 'mobx';
import { observer } from 'mobx-react-lite';
import useImmutablePeriod from 'global/hooks/useImmutablePeriod';
import { SupportedUserRoles } from '@insurancemenu/imenu-components-lib/dist/types';
import { USER_ACTIONS } from 'types/enrollmentService';
import { FormStatuses } from 'types/formService';
import { ProductLineId } from '@insurancemenu/imenu-components-lib/dist/types/ProductLineId';
import { requestApplyUserAction } from 'api/operations/enrollmentService';
import WebFormsTableRow from '../WebFormsTableRow';
import WebFormsTableHeader from '../WebFormsTableHeader';
import { createStyles } from './styles';

export interface EmployerFormInstance {
  enrollmentPeriodId: string;
  id: string;
  form: EmployerForm;
  productLineIds: ProductLineId[];
  status: FormStatuses;
}

interface EmployerForm {
  id: string;
  formTitle: string;
  roleAvailability: SupportedUserRoles[];
}

interface WebFormsTableProps {
  formInstances: EmployerFormInstance[];
  userAction: USER_ACTIONS;
  loading: boolean;
  redirectLink: string;
}

const WebFormsTable = observer(
  ({ formInstances, userAction, loading, redirectLink }: WebFormsTableProps): JSX.Element => {
    const classes = createStyles();
    const history = useHistory();
    const { periodId } = useParams<{ periodId: string }>();

    // Mobx store
    const {
      userStore: { userRoles },
    } = useRootStore();

    const isPeriodNotEditable = useImmutablePeriod(periodId);
    const isFormsCompleted = formInstances.every((form) => form.status === FormStatuses.COMPLETE);

    async function completeHandler(): Promise<void> {
      if (isFormsCompleted && periodId) {
        await requestApplyUserAction(periodId, userAction);
        runInAction(() => {
          history.push(`/checklist/${periodId}`);
        });
      }
    }

    const completeFormsButton = (
      <Button
        size="large"
        color="primary"
        width="24rem"
        className={classes.completeFormButton}
        disabled={!isFormsCompleted}
        onClick={completeHandler}
      >
        Complete Forms
      </Button>
    );

    const exitButton = (
      <Button
        size="large"
        type="outlined"
        width="24rem"
        className={classes.completeFormButton}
        onClick={(): void => history.push(`/checklist/${periodId}`)}
      >
        Exit
      </Button>
    );

    return (
      <div className={classes.pageWrapper}>
        <div className={classes.tableWrapper}>
          <Table loading={loading} totalElements={formInstances.length} maxHeight="auto">
            <WebFormsTableHeader />
            <TableBody className={classes.tableBody}>
              {formInstances
                .sort((a, b) => (a.form.formTitle > b.form.formTitle ? 1 : -1))
                .map((form) => (
                  <WebFormsTableRow
                    key={form.id}
                    formInstance={form}
                    userRoles={userRoles}
                    redirectLink={redirectLink}
                  />
                ))}
            </TableBody>
          </Table>
        </div>
        {isPeriodNotEditable ? exitButton : completeFormsButton}
      </div>
    );
  },
);

export default WebFormsTable;
